import type { ReactElement, ReactNode } from 'react';
import { isTaskActive, TaskStatus, type TaskDto } from '@gr/core/platform/tasks/dto';
import { Link, useT } from '../../../../app/di';
import { formatDateTime, formatFileSize } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { ProgressBar } from '../../../../ui-kit/ProgressBar/ProgressBar';
import { cancellable, resumable, taskFraction, taskKindKey, taskTone } from '../model/task-view';
import styles from './TaskCard.module.scss';

export interface TaskCardProps {
  task: TaskDto;
  /** Часовой пояс блога для дат; без него — пояс браузера. */
  timezone?: string;
  /** Показать блог задачи (раздел администратора). */
  showBlog?: boolean;
  /** Компактно — в списке индикатора: без автора и дат. */
  compact?: boolean;
  /** TASK-RULE-3: может ли пользователь отменять и возобновлять. */
  canManage?: boolean;
  pending?: boolean;
  onCancel?(task: TaskDto): void;
  onResume?(task: TaskDto): void;
  /** Ссылка на отчёт задачи блога (TASK-UC-3). */
  withReportLink?: boolean;
  /** Параметры и итог вида задачи — на странице отчёта. */
  children?: ReactNode;
}

/** TASK-UC-2, TASK-UC-3: фоновая задача — что делается, сколько сделано, над чем работа сейчас. */
export function TaskCard({ task, timezone, showBlog = false, compact = false, canManage = false, pending = false, onCancel, onResume, withReportLink = false, children }: TaskCardProps): ReactElement {
  const { t, tp, te, locale } = useT();
  const title = t(`tasks.kinds.${taskKindKey(task.kind)}`);
  const fraction = taskFraction(task);
  const active = isTaskActive(task.status);
  const { progress } = task;
  const status = task.status === TaskStatus.Running && task.cancelRequested ? t('tasks.card.cancelling') : t(`tasks.statuses.${task.status}`);
  const current = progress.current;
  const counters: string[] = [];
  if (progress.total > 0) counters.push(t('tasks.card.steps', { done: progress.done, total: progress.total }));
  if (progress.bytesTotal) counters.push(t('tasks.card.volume', { done: formatFileSize(progress.bytesDone ?? 0, locale), total: formatFileSize(progress.bytesTotal, locale) }));
  if (progress.failed > 0) counters.push(tp('tasks.card.failed', progress.failed, { n: progress.failed }));
  if (progress.skipped > 0) counters.push(tp('tasks.card.skipped', progress.skipped, { n: progress.skipped }));

  return (
    <article className={styles.card} aria-label={title} data-task-status={task.status}>
      <div className={styles.head}>
        <strong className={styles.title}>{title}</strong>
        <Badge tone={taskTone(task)}>{status}</Badge>
      </div>
      {showBlog ? <p className={styles.blog}>{task.blogName ?? t('tasks.card.platform')}</p> : null}
      {active && fraction !== null ? <ProgressBar value={fraction} label={t('tasks.card.progress', { title })} /> : null}
      {counters.length > 0 ? <p className={styles.counters}>{counters.join(' · ')}</p> : null}
      {active && current?.label ? (
        <p className={styles.current}>
          {current.fraction === null
            ? t('tasks.card.current', { label: current.label })
            : t('tasks.card.currentShare', { label: current.label, percent: Math.round(current.fraction * 100) })}
        </p>
      ) : null}
      {task.status === TaskStatus.Paused && task.pauseReason ? <p className={styles.reason}>{t('tasks.card.pauseReason', { reason: te(task.pauseReason) })}</p> : null}
      {compact ? null : (
        <p className={styles.meta}>
          {task.createdBy
            ? t('tasks.card.createdBy', { name: task.createdBy.displayName, at: formatDateTime(task.createdAt, locale, timezone) })
            : t('tasks.card.createdBySystem', { at: formatDateTime(task.createdAt, locale, timezone) })}
          {task.finishedAt ? ` · ${t('tasks.card.finishedAt', { at: formatDateTime(task.finishedAt, locale, timezone) })}` : null}
        </p>
      )}
      {children}
      {(canManage && (cancellable(task) || resumable(task))) || (withReportLink && task.blogId) ? (
        <div className={styles.actions}>
          {withReportLink && task.blogId ? (
            <Link to="task" params={{ blogId: task.blogId, taskId: task.id }} className={styles.link}>
              {t('tasks.card.report')}
            </Link>
          ) : null}
          {canManage && resumable(task) ? (
            <Button size="sm" variant="primary" disabled={pending} onClick={() => onResume?.(task)}>
              {t('tasks.card.resume')}
            </Button>
          ) : null}
          {canManage && cancellable(task) ? (
            <Button size="sm" variant="ghost" disabled={pending} onClick={() => onCancel?.(task)}>
              {t('tasks.card.cancel')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
